/**
 * @file modules/extension_reviews_carousel.js
 * @description Prev/next arrow scrolling for the reviews carousel on the /extension
 *              reskin page. Built as a finite alternative to fragno-carousel, which
 *              forces infinite-nav clone-based wrap-around whenever shownavbar is set.
 */

import { dispatchTrack } from "../analytics/shop-analytics-track.js";

const SCROLL_TOLERANCE_PX = 4;

// Shared AbortController so repeated init calls on one document (the test
// suite inits per example) clean up the previous set of listeners before
// wiring new ones, instead of leaking a handler on window.resize every time.
let currentController = null;

export function initExtensionReviewsCarousel() {
  currentController?.abort();
  currentController = new AbortController();
  const { signal } = currentController;

  const root = document.querySelector(".extension-reviews");
  if (!root) return;

  const track = root.querySelector(".extension-reviews__track");
  const prev = root.querySelector(".extension-reviews__arrow--prev");
  const next = root.querySelector(".extension-reviews__arrow--next");
  if (!track || !prev || !next) return;

  const scrollStep = () => {
    const firstCard = track.firstElementChild;
    if (!firstCard) return track.clientWidth;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
    return cardWidth + gap;
  };

  const prefersReducedMotion = () =>
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const step = (direction) => {
    track.scrollBy({
      left: direction * scrollStep(),
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
    dispatchTrack(root, "extension_reviews_nav", {
      source: "Extension Landing",
      direction: direction > 0 ? "next" : "prev",
    });
  };

  prev.addEventListener("click", () => step(-1), { signal });
  next.addEventListener("click", () => step(1), { signal });

  const setDisabled = (button, disabled) => {
    button.disabled = disabled;
    button.setAttribute("aria-disabled", String(disabled));
  };

  const updateButtons = () => {
    const atStart = track.scrollLeft <= SCROLL_TOLERANCE_PX;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atEnd = track.scrollLeft >= maxScroll - SCROLL_TOLERANCE_PX;
    setDisabled(prev, atStart);
    setDisabled(next, atEnd || maxScroll <= 0);
  };

  track.addEventListener("scroll", updateButtons, { passive: true, signal });
  window.addEventListener("resize", updateButtons, { signal });
  updateButtons();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initExtensionReviewsCarousel);
} else {
  initExtensionReviewsCarousel();
}
